import type { ExecutionDoc, ExecutionStatus, TaskDoc } from '@mct/shared';

/**
 * Statuses that count as "predecessor finished" for the purpose of
 * unblocking a downstream task. `skipped` is intentionally included:
 * an explicit skip is the user telling us this step is N/A for this
 * run, so successors should be free to proceed.
 */
const PREDECESSOR_READY: ReadonlySet<ExecutionStatus> = new Set([
  'done',
  'skipped',
]);

/**
 * Statuses that imply the row has moved past `not_started`. Picking
 * any of these from `not_started` requires predecessors to be ready.
 */
const FORWARD_STATUSES: ReadonlySet<ExecutionStatus> = new Set([
  'in_progress',
  'blocked',
  'done',
  'skipped',
]);

export interface BlockedTransition {
  /**
   * Predecessor task ids whose execution status is not in
   * `{done, skipped}`. Caller can use this to highlight blocking
   * cards in the canvas.
   */
  blockingPredecessorIds: string[];
  /** Localised explanation surfaced as tooltip / toast. */
  message: string;
}

/**
 * Decide whether `current → next` is allowed given the predecessor
 * graph. Returns `null` when the change is allowed; a descriptive
 * `BlockedTransition` when it is not.
 *
 * Policy:
 *   - Same-status transitions are no-ops (allowed).
 *   - The gate fires ONLY on the first step out of `not_started`. The
 *     first move (start / skip / direct-mark-done / mark-blocked)
 *     requires every `dependsOn` predecessor's run-execution status
 *     to be in `{done, skipped}`. A predecessor with no execution
 *     record is also treated as not-ready (defensive default).
 *   - Once a row has left `not_started`, all subsequent transitions
 *     are free. This includes reverting to `not_started`, switching
 *     between `in_progress` / `blocked` / `done` / `skipped`, or
 *     reopening a `done` row — the row already represents work that
 *     began, so blocking edits at this point would punish the user
 *     for rare data drift (e.g. upstream rolled back manually).
 *
 * Skip semantics: `skipped` counts as both a forward transition (so
 * you can't skip mid-chain when upstream is pending) AND a
 * predecessor-ready state (downstream can proceed past a skipped row).
 */
export function checkStatusTransition(
  next: ExecutionStatus,
  current: ExecutionStatus,
  task: Pick<TaskDoc, 'dependsOn'>,
  executionByTaskId: ReadonlyMap<string, Pick<ExecutionDoc, 'status'>>,
  taskNameById?: ReadonlyMap<string, string>,
): BlockedTransition | null {
  if (next === current) return null;
  // Only the first step out of not_started is gated.
  if (current !== 'not_started') return null;
  if (!FORWARD_STATUSES.has(next)) return null;

  const blocking: string[] = [];
  for (const predId of task.dependsOn) {
    const exec = executionByTaskId.get(predId);
    if (!exec || !PREDECESSOR_READY.has(exec.status)) {
      blocking.push(predId);
    }
  }
  if (blocking.length === 0) return null;

  const names = blocking.map((id) => taskNameById?.get(id) ?? id);
  const sample = names[0]!;
  const more = names.length > 1 ? ` ほか ${names.length - 1} 件` : '';
  return {
    blockingPredecessorIds: blocking,
    message: `前のタスクが完了/スキップされていないため変更できません: ${sample}${more}`,
  };
}
